import { LoadState } from "@redotech/react-util/load";

/** A flag-gated LoadState: `undefined` when the flag is off (caller shows its
 *  legacy value), else pending / error / a ready value (with an empty fallback). */
export function flagGatedLoad<T>({
  flagEnabled,
  pending,
  error,
  data,
  empty,
}: {
  flagEnabled: boolean;
  pending: boolean;
  error: unknown;
  data: T | undefined;
  empty: T;
}): LoadState<T> | undefined {
  if (!flagEnabled && !pending) {
    return undefined;
  }
  if (pending) {
    return { pending: true };
  }
  if (error != null) {
    return { pending: false, error };
  }
  return { pending: false, value: data ?? empty };
}

/** Shown in place of a figure the attribution source could not produce. Matches
 *  what the campaign page already shows for a failed unsubscribes read. */
export const ATTRIBUTION_ERROR_TEXT = "Error";

/** The text to render for one attributed figure: the legacy value when the flag
 *  is off, else the load carried through. An omitted entity drove nothing. */
export function attributionDisplay<T, V>({
  load,
  select,
  legacy,
  format,
}: {
  load: LoadState<T> | undefined;
  select: (data: T) => V | undefined;
  legacy: string;
  format: (value: V | undefined) => string;
}): LoadState<string> {
  if (!load) {
    return { pending: false, value: legacy };
  }
  if (load.pending) {
    return { pending: true };
  }
  // Falling back to the legacy total would serve a number from the source the
  // flag turns off, with nothing on screen saying so.
  if (load.value === undefined) {
    return { pending: false, error: load.error ?? new Error("No attribution") };
  }
  return { pending: false, value: format(select(load.value)) };
}
